// src/features/analytics/components/ViewerMixTrendCard.tsx
//
// "New vs returning" — stacked daily session mix on the Overview (metrics
// expansion). Reuses the page's playback/daily response (extend, don't
// refetch); only days carrying the mix fields chart (viewerMix.ts gap-fills
// inside the measured window for axis continuity). The cookie caveat caption
// is REQUIRED honesty — the split undercounts returning viewers by design.

import { Paper, Skeleton, Text, Title } from '@mantine/core';
import { AreaChart } from '@mantine/charts';
import type { UtcRange } from '@/shared/utils/utcRange';
import { AnalyticsUtils } from '../AnalyticsUtils';
import { trendTickInterval, utcDayLabel } from '../playbackTrend';
import { viewerMixSeries } from '../viewerMix';
import { AnalyticsCoverageNotice } from './AnalyticsCoverageNotice';
import { AnalyticsErrorState } from './AnalyticsErrorState';
import type { AnalyticsEnvelope, PlaybackDailyData } from '../types';

interface ViewerMixTrendCardProps {
  playbackDaily: AnalyticsEnvelope<PlaybackDailyData> | null;
  range: UtcRange;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
}

interface MixChartRow {
  day: string;
  sessionsNew: number;
  sessionsReturning: number;
}

const COOKIE_CAVEAT =
  'Measured for browsers that retain the viewer cookie; private-mode and cookie-blocking browsers count as new.';

function MixTooltip({ label, payload }: { label?: unknown; payload?: unknown }) {
  const rows = payload as { payload?: MixChartRow }[] | undefined;
  const row = rows?.[0]?.payload;
  if (!row) return null;
  const total = row.sessionsNew + row.sessionsReturning;
  return (
    <Paper withBorder shadow="sm" p="xs">
      <Text size="xs" fw={600}>
        {String(label)} (UTC)
      </Text>
      <Text size="xs">New: {AnalyticsUtils.formatCount(row.sessionsNew)}</Text>
      <Text size="xs">Returning: {AnalyticsUtils.formatCount(row.sessionsReturning)}</Text>
      {total > 0 && (
        <Text size="xs" c="dimmed">
          Returning share: {AnalyticsUtils.formatPercent(row.sessionsReturning / total)}
        </Text>
      )}
    </Paper>
  );
}

export function ViewerMixTrendCard({
  playbackDaily,
  range,
  isLoading,
  isError,
  error,
  onRetry,
}: ViewerMixTrendCardProps) {
  const series = viewerMixSeries(playbackDaily?.data.points ?? []);
  const chartData: MixChartRow[] = series.map((d) => ({
    day: utcDayLabel(d.day),
    sessionsNew: d.sessionsNew,
    sessionsReturning: d.sessionsReturning,
  }));

  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        New vs returning
      </Title>
      {isLoading ? (
        <Skeleton height={220} />
      ) : isError ? (
        <AnalyticsErrorState error={error} onRetry={onRetry} />
      ) : series.length === 0 ? (
        <AnalyticsCoverageNotice
          coverage={playbackDaily?.coverage}
          freshness={playbackDaily?.freshness}
          range={range}
          emptyMessage="No new-vs-returning data for this range yet. The daily viewer mix appears once a day is materialized."
        />
      ) : (
        <>
          <AnalyticsCoverageNotice
            coverage={playbackDaily?.coverage}
            freshness={playbackDaily?.freshness}
            range={range}
          />
          <AreaChart
            h={220}
            data={chartData}
            dataKey="day"
            type="stacked"
            series={[
              { name: 'sessionsNew', label: 'New', color: 'blue.6' },
              { name: 'sessionsReturning', label: 'Returning', color: 'cyan.4' },
            ]}
            curveType="monotone"
            withDots={false}
            withLegend
            xAxisProps={{ interval: trendTickInterval(series.length) }}
            tooltipProps={{
              content: ({ label, payload }) => <MixTooltip label={label} payload={payload} />,
            }}
          />
          <Text size="xs" c="dimmed" mt={4}>
            {COOKIE_CAVEAT}
          </Text>
        </>
      )}
    </Paper>
  );
}
